/**
 * pages/Help.js — Phase 84.15
 *
 * The in-product Help Center. Two-column layout on desktop
 * (categories left, article right). Deep-linked via URL:
 *    /help                      → landing (first article of first category)
 *    /help/:categorySlug        → first article of that category
 *    /help/:categorySlug/:slug  → specific article
 *
 * Loads the manifest once, then each article body on-demand.
 * SuperAdmins see inline Edit buttons on every article.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import usePageTitle from '@/hooks/usePageTitle';
import { useAuth } from '@/context/AuthContext';
import { fetchManifest, fetchArticle } from '@/lib/helpApi';
import HelpCategoryList from '@/components/help/HelpCategoryList';
import HelpSearchBar from '@/components/help/HelpSearchBar';
import HelpArticle from '@/components/help/HelpArticle';

export default function Help() {
  usePageTitle('Help');
  const nav = useNavigate();
  const params = useParams();
  const { profile } = useAuth();
  const isSuperadmin = !!profile?.is_superadmin;

  const [manifest, setManifest] = useState(null);
  const [manifestErr, setManifestErr] = useState(null);
  const [article, setArticle] = useState(null);
  const [articleErr, setArticleErr] = useState(null);
  const [loadingArticle, setLoadingArticle] = useState(false);

  // Load manifest once.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const m = await fetchManifest();
        if (!cancelled) setManifest(m);
      } catch (e) {
        if (!cancelled) setManifestErr(e.message || 'Could not load help.');
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Resolve which article should be shown based on URL + manifest.
  const activeCategorySlug = params.categorySlug || manifest?.categories?.[0]?.slug || null;
  const activeArticleSlug = useMemo(() => {
    if (!manifest) return null;
    if (params.articleSlug) return params.articleSlug;
    const cat = manifest.categories.find((c) => c.slug === activeCategorySlug) || manifest.categories[0];
    return cat?.articles?.[0]?.slug || null;
  }, [manifest, params.articleSlug, activeCategorySlug]);

  // Load the active article body.
  useEffect(() => {
    if (!activeArticleSlug) return;
    let cancelled = false;
    setLoadingArticle(true);
    setArticleErr(null);
    (async () => {
      try {
        const a = await fetchArticle(activeArticleSlug);
        if (!cancelled) setArticle(a);
      } catch (e) {
        if (!cancelled) {
          setArticleErr(e.status === 404 ? 'Article not found.' : (e.message || 'Could not load this article.'));
          setArticle(null);
        }
      } finally {
        if (!cancelled) setLoadingArticle(false);
      }
    })();
    return () => { cancelled = true; };
  }, [activeArticleSlug]);

  const goTo = (categorySlug, slug) => {
    if (categorySlug && slug) nav(`/help/${categorySlug}/${slug}`);
    else if (categorySlug) nav(`/help/${categorySlug}`);
    else nav('/help');
  };

  const onArticleUpdated = (updated) => {
    setArticle(updated);
    // Also patch the local manifest so the sidebar title reflects the edit.
    if (manifest) {
      const next = {
        ...manifest,
        categories: manifest.categories.map((c) => ({
          ...c,
          articles: (c.articles || []).map((a) =>
            a.slug === updated.slug ? { ...a, title: updated.title, summary: updated.summary } : a,
          ),
        })),
      };
      setManifest(next);
    }
  };

  if (manifestErr) {
    return (
      <div className="max-w-3xl mx-auto py-12 px-6">
        <div className="bg-white rounded-2xl border border-border p-6">
          <div className="font-medium">Couldn’t load the Help Center</div>
          <div className="text-sm text-muted-foreground mt-1">{manifestErr}</div>
          <div className="text-xs text-muted-foreground mt-3">If this persists, contact support at hello@2120.money.</div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 md:px-6 py-6 md:py-8">
      <header className="mb-6">
        <h1 className="font-display text-2xl md:text-3xl font-semibold tracking-tight">Help Center</h1>
        <p className="text-sm text-muted-foreground mt-1">Everything you need to get value from 2120.</p>
      </header>

      <div className="mb-6 max-w-2xl">
        <HelpSearchBar manifest={manifest} onPick={goTo} />
      </div>

      <div className="grid md:grid-cols-[260px_1fr] gap-6 md:gap-8">
        <aside className="md:sticky md:top-24 md:h-fit">
          <HelpCategoryList
            manifest={manifest}
            activeCategorySlug={activeCategorySlug}
            activeArticleSlug={activeArticleSlug}
            onSelect={goTo}
          />
        </aside>

        <main className="min-w-0">
          {loadingArticle && !article ? (
            <div className="bg-white rounded-2xl border border-border p-8 text-sm text-muted-foreground">
              Loading article…
            </div>
          ) : articleErr ? (
            <div className="bg-white rounded-2xl border border-border p-8">
              <div className="font-medium">{articleErr}</div>
              <div className="text-xs text-muted-foreground mt-2">Try picking another article from the sidebar.</div>
            </div>
          ) : article ? (
            <HelpArticle article={article} isSuperadmin={isSuperadmin} onUpdated={onArticleUpdated} />
          ) : null}
        </main>
      </div>
    </div>
  );
}
